import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { ClipboardList, RefreshCcw, ShieldAlert, AlertTriangle, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only audit log viewer. Backed by GET /api/auth/security/audit-log.
 * Kept as its own component (not inlined in DashboardPage) so the parent
 * doesn't grow further.
 */
const PAGE_SIZE = 25;

const AuditLogCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [sinceHours, setSinceHours] = useState("168"); // 7 days
  const [actorFilter, setActorFilter] = useState("");
  const [page, setPage] = useState(0);

  const fetchLog = async (nextPage = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        since_hours: String(sinceHours),
        limit: String(PAGE_SIZE),
        offset: String(nextPage * PAGE_SIZE),
      });
      if (eventType !== "all") params.set("event_type", eventType);
      if (severity !== "all") params.set("severity", severity);
      if (actorFilter.trim()) params.set("actor", actorFilter.trim());

      const res = await axios.get(`${API}/auth/security/audit-log?${params}`);
      setData(res.data);
    } catch (e) {
      console.error("audit log fetch failed", e);
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  // Any filter change resets to page 0 and reloads
  useEffect(() => {
    setPage(0);
    fetchLog(0);
  }, [eventType, severity, sinceHours]);

  // Page change reloads
  useEffect(() => {
    fetchLog(page);
  }, [page]);

  // Grouped event-type dropdown: keep dropdown short by grouping by prefix.
  const eventOptions = useMemo(() => {
    const known = data?.known_event_types || [];
    // Bucket by prefix before the "."
    const groups = new Map();
    known.forEach((t) => {
      const [prefix] = t.split(".");
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix).push(t);
    });
    return { known, groups: Array.from(groups.entries()) };
  }, [data]);

  const events = data?.events || [];
  const totals = data?.severity_totals || { info: 0, warn: 0, alert: 0 };
  const total = data?.total ?? 0;
  const pageStart = page * PAGE_SIZE + (events.length ? 1 : 0);
  const pageEnd = page * PAGE_SIZE + events.length;
  const hasPrev = page > 0;
  const hasNext = pageEnd < total;

  const applyActor = () => {
    setPage(0);
    fetchLog(0);
  };

  return (
    <Card
      className="bg-slate-800/50 border-slate-700"
      data-testid="audit-log-card"
    >
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-saturn-orange" />
            Audit Log
            <Badge
              variant="outline"
              className="border-slate-600 text-slate-300 text-[10px] ml-2"
              data-testid="audit-log-count"
            >
              {events.length} events
            </Badge>
            {total > 0 && (
              <Badge
                variant="outline"
                className="border-slate-700 text-slate-400 text-[10px]"
                data-testid="audit-log-total"
              >
                of {total}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1 text-red-400">
              <ShieldAlert className="w-3.5 h-3.5" />
              {totals.alert} alert
            </span>
            <span className="flex items-center gap-1 text-yellow-400">
              <AlertTriangle className="w-3.5 h-3.5" />
              {totals.warn} warn
            </span>
            <span className="flex items-center gap-1 text-slate-400">
              <Info className="w-3.5 h-3.5" />
              {totals.info} info
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchLog(page)}
              className="border-slate-700 text-slate-300 h-7 px-2"
              data-testid="audit-log-refresh-btn"
            >
              <RefreshCcw className="w-3 h-3 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger
              className="bg-slate-900/50 border-slate-700 text-white h-9"
              data-testid="audit-log-event-filter"
            >
              <SelectValue placeholder="Event type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {eventOptions.groups.map(([prefix, items]) => (
                <React.Fragment key={prefix}>
                  <SelectItem value={prefix}>
                    {prefix}.* (group)
                  </SelectItem>
                  {items.map((t) => (
                    <SelectItem key={t} value={t}>
                      &nbsp;&nbsp;{t}
                    </SelectItem>
                  ))}
                </React.Fragment>
              ))}
            </SelectContent>
          </Select>

          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger
              className="bg-slate-900/50 border-slate-700 text-white h-9"
              data-testid="audit-log-severity-filter"
            >
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              <SelectItem value="alert">Alert only</SelectItem>
              <SelectItem value="warn">Warn only</SelectItem>
              <SelectItem value="info">Info only</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sinceHours} onValueChange={setSinceHours}>
            <SelectTrigger
              className="bg-slate-900/50 border-slate-700 text-white h-9"
              data-testid="audit-log-window-filter"
            >
              <SelectValue placeholder="Time window" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
              <SelectItem value="720">Last 30 days</SelectItem>
              <SelectItem value="2160">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <Input
              placeholder="Filter by actor / IP…"
              value={actorFilter}
              onChange={(e) => setActorFilter(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyActor()}
              className="bg-slate-900/50 border-slate-700 text-white h-9"
              data-testid="audit-log-actor-filter"
            />
            <Button
              size="sm"
              onClick={applyActor}
              className="bg-saturn-orange hover:bg-saturn-orange/80 h-9"
              data-testid="audit-log-apply-actor-btn"
            >
              Apply
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center text-slate-400 py-8">
            Loading audit events…
          </div>
        ) : events.length === 0 ? (
          <div
            className="text-center text-slate-400 py-8"
            data-testid="audit-log-empty"
          >
            No events match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400 whitespace-nowrap">
                    When
                  </TableHead>
                  <TableHead className="text-slate-400">Event</TableHead>
                  <TableHead className="text-slate-400">Actor / IP</TableHead>
                  <TableHead className="text-slate-400">Target</TableHead>
                  <TableHead className="text-slate-400">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev) => (
                  <AuditRow key={ev.event_id} ev={ev} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination controls — shown whenever there's more than one page */}
        {!loading && total > PAGE_SIZE && (
          <div
            className="flex items-center justify-between pt-3 border-t border-slate-800"
            data-testid="audit-log-pagination"
          >
            <div className="text-xs text-slate-400" data-testid="audit-log-range">
              {pageStart}–{pageEnd} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!hasPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="border-slate-700 text-slate-300 h-7 px-3 disabled:opacity-40"
                data-testid="audit-log-prev-btn"
              >
                Previous
              </Button>
              <span className="text-xs text-slate-500 tabular-nums px-1">
                Page {page + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={!hasNext}
                onClick={() => setPage((p) => p + 1)}
                className="border-slate-700 text-slate-300 h-7 px-3 disabled:opacity-40"
                data-testid="audit-log-next-btn"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const severityStyles = {
  alert: "bg-red-500/10 text-red-400 border-red-500/40",
  warn: "bg-yellow-500/10 text-yellow-400 border-yellow-500/40",
  info: "bg-slate-500/10 text-slate-300 border-slate-500/40",
};

const AuditRow = ({ ev }) => {
  const [open, setOpen] = useState(false);
  const when = new Date(ev.created_at);
  const whenLabel = when.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const sevStyle = severityStyles[ev.severity] || severityStyles.info;
  const metaCount = Object.keys(ev.meta || {}).length;

  return (
    <>
      <TableRow
        className="border-slate-800 hover:bg-slate-900/40"
        data-testid={`audit-row-${ev.event_id}`}
      >
        <TableCell className="text-slate-300 text-xs whitespace-nowrap align-top">
          {whenLabel}
        </TableCell>
        <TableCell className="align-top">
          <div className="flex items-center gap-2">
            <Badge className={`${sevStyle} border text-[10px]`}>
              {ev.severity}
            </Badge>
            <span className="text-white text-xs font-mono">
              {ev.event_type}
            </span>
          </div>
          <div className="text-slate-400 text-xs mt-1">{ev.message}</div>
        </TableCell>
        <TableCell className="text-slate-300 text-xs align-top">
          <div className="truncate max-w-[180px]">{ev.actor || "—"}</div>
          {ev.ip ? (
            <div className="text-slate-500 text-[10px] font-mono">{ev.ip}</div>
          ) : null}
        </TableCell>
        <TableCell className="text-slate-300 text-xs align-top">
          <div className="truncate max-w-[180px]" title={ev.target}>
            {ev.target || "—"}
          </div>
        </TableCell>
        <TableCell className="align-top">
          {metaCount > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs text-slate-300 hover:text-white"
              onClick={() => setOpen((v) => !v)}
              data-testid={`audit-row-toggle-${ev.event_id}`}
            >
              {open ? "Hide" : "Show"} ({metaCount})
            </Button>
          ) : (
            <span className="text-slate-500 text-xs">—</span>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="border-slate-800 bg-slate-900/60">
          <TableCell colSpan={5} className="text-xs">
            <pre className="text-slate-300 font-mono text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(ev.meta, null, 2)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default AuditLogCard;
